import { useEffect, useState } from "react";
import { copy } from "@wheels-crm/shared-types";
import type { Database } from "@wheels-crm/shared-types";
import { supabase } from "../lib/supabaseClient";
import { Button } from "../components/ui/Button";

type OrderRow = Database["public"]["Tables"]["orders"]["Row"];
type ClientRow = Database["public"]["Tables"]["clients"]["Row"];
type OrderWithClient = OrderRow & { client: ClientRow };

type CategoryKey = "price" | "not_relevant" | "placed";

const CATEGORIES: { key: CategoryKey; label: string }[] = [
  { key: "price", label: copy.leadsCategory.price },
  { key: "not_relevant", label: copy.leadsCategory.not_relevant },
  { key: "placed", label: copy.leadsCategory.placed },
];

function matchesCategory(order: OrderRow, key: CategoryKey): boolean {
  if (key === "placed") return order.status !== "rejected";
  return order.status === "rejected" && order.reject_reason === key;
}

/** Dedupes by phone number — a client only appears once per category even with multiple orders (§6.8). */
function dedupeByPhone(orders: OrderWithClient[]): { name: string; phone: string }[] {
  const seen = new Map<string, { name: string; phone: string }>();
  for (const o of orders) {
    if (!seen.has(o.client.phone)) {
      seen.set(o.client.phone, { name: o.client.name, phone: o.client.phone });
    }
  }
  return [...seen.values()];
}

function downloadCsv(category: CategoryKey, rows: { phone: string }[]) {
  const date = new Date().toISOString().slice(0, 10);
  const csv = ["phone", ...rows.map((r) => r.phone)].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `leads_${category}_${date}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function LeadsPage() {
  const [orders, setOrders] = useState<OrderWithClient[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("orders")
      .select("*, client:clients(*)")
      .then(({ data }) => {
        setOrders((data ?? []) as unknown as OrderWithClient[]);
        setLoading(false);
      });
  }, []);

  return (
    <div>
      <div className="page-header">
        <h1>{copy.leads.title}</h1>
      </div>

      {loading ? (
        <p>Завантаження…</p>
      ) : (
        <div className="widget-grid">
          {CATEGORIES.map(({ key, label }) => {
            const matched = orders.filter((o) => matchesCategory(o, key));
            const clients = dedupeByPhone(matched);
            return (
              <div className="card" key={key}>
                <h2 style={{ fontSize: 15, marginTop: 0 }}>
                  {label} ({clients.length})
                </h2>
                {clients.length === 0 ? (
                  <p className="empty-state">{copy.emptyState}</p>
                ) : (
                  <>
                    <div style={{ maxHeight: 260, overflowY: "auto", marginBottom: 12 }}>
                      {clients.map((c) => (
                        <div key={c.phone} style={{ fontSize: 13, padding: "3px 0" }}>
                          {c.name} — {c.phone}
                        </div>
                      ))}
                    </div>
                    <Button onClick={() => downloadCsv(key, clients)}>{copy.leads.downloadButton}</Button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
